import type { AxiosError } from 'axios';
import { createMutation } from 'react-query-kit';

import type { Car } from '@/api/cars/types';
import { client } from '@/api/common';

type Variables = {
  id: number;
  allowSubscription: boolean;
  autopilotType: string;
  color: string;
  dailyRate: string;
  deliveryPersonPhone: string;
  gloveboxPin: string;
  hoursBetweenTrips: string;
  licensePlate: string;
  licensePlateState: string;
  maxDeliveryDistance: string;
  maximumDailyRate: string;
  minDaysToBook: string;
  minHoursInAdvance: string;
  minimumDailyRate: string;
  numSeats: string;
  oneDayMultiplier: string;
  defaultLocationId: string;
  supportsDelivery: boolean;
  trimId: string;
  useAutomatedPricing: boolean;
};
export type UpdateLocationResponse = {
  data: Car;
};

export const useUpdateCar = createMutation<
  UpdateLocationResponse,
  Variables,
  AxiosError
>({
  mutationFn: async (variables) => {
    const data: Record<string, any> = {};

    if (variables.allowSubscription !== undefined)
      data.allow_subscription = variables.allowSubscription;

    if (variables.autopilotType !== undefined)
      data.autopilot_type = parseInt(variables.autopilotType, 10);

    if (variables.color !== undefined)
      data.color = parseInt(variables.color, 10);

    if (variables.dailyRate !== undefined)
      data.daily_rate = parseFloat(variables.dailyRate);

    if (variables.deliveryPersonPhone !== undefined)
      data.delivery_person_phone = variables.deliveryPersonPhone;

    if (variables.hoursBetweenTrips !== undefined)
      data.hours_between_trips = parseInt(variables.hoursBetweenTrips, 10);

    if (variables.maxDeliveryDistance !== undefined)
      data.max_delivery_distance = parseInt(variables.maxDeliveryDistance, 10);

    if (variables.maximumDailyRate !== undefined)
      data.maximum_daily_rate = parseFloat(variables.maximumDailyRate);

    if (variables.minDaysToBook !== undefined)
      data.min_days_to_book = parseInt(variables.minDaysToBook, 10);

    if (variables.minHoursInAdvance !== undefined)
      data.min_hours_in_advance = parseInt(variables.minHoursInAdvance, 10);

    if (variables.minimumDailyRate !== undefined)
      data.minimum_daily_rate = parseFloat(variables.minimumDailyRate);

    if (variables.numSeats !== undefined)
      data.num_seats = parseInt(variables.numSeats, 10);

    if (variables.oneDayMultiplier !== undefined)
      data.one_day_multiplier = Number(
        (parseFloat(variables.oneDayMultiplier) / 100).toFixed(2)
      );

    if (variables.supportsDelivery !== undefined)
      data.supports_delivery = variables.supportsDelivery;

    return client({
      url: `v2/internal/partners/cars/${variables.id}`,
      method: 'PUT',
      data: {
        ...data,
        glovebox_pin: variables.gloveboxPin,
        license_plate: variables.licensePlate,
        license_plate_state: parseInt(variables.licensePlateState, 10),
        default_location_id: parseInt(variables.defaultLocationId, 10),
        trim_id: parseInt(variables.trimId, 10),
        use_automated_pricing: variables.useAutomatedPricing,
      },
    })
      .then((response) => response.data)
      .catch((err: any) => {
        throw new Error(err.response.data.error);
      });
  },
});
